<template>
  <div class="relative h-screen pb-16">
    <!-- Map Container -->
    <div class="absolute inset-0 bg-gray-100">
      <!-- Placeholder for actual map -->
      <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
        <div class="text-center">
          <span class="text-6xl mb-4 block">🗺️</span>
          <p class="text-gray-600">Interactive Map View</p>
          <p class="text-sm text-gray-400">GPS: 13.7563° N, 100.5018° E</p>
        </div>
      </div>
    </div>

    <!-- Map Controls -->
    <div class="absolute top-4 left-4 right-4 z-10">
      <!-- Search Bar -->
      <div class="bg-white rounded-lg shadow-lg p-3 mb-3">
        <div class="flex items-center space-x-2">
          <div class="flex-1 relative">
            <input 
              type="text" 
              placeholder="Search fields, devices, or locations..."
              class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
            <span class="absolute left-3 top-2.5 text-gray-400">🔍</span>
          </div>
          <button class="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <span>📍</span>
          </button>
        </div>
      </div>

      <!-- Filter Pills -->
      <div class="flex space-x-2 overflow-x-auto pb-2">
        <button class="px-3 py-1 bg-green-600 text-white rounded-full text-xs whitespace-nowrap">
          All Fields
        </button>
        <button class="px-3 py-1 bg-white text-gray-600 rounded-full text-xs whitespace-nowrap">
          Active Drones
        </button>
        <button class="px-3 py-1 bg-white text-gray-600 rounded-full text-xs whitespace-nowrap">
          No-Fly Zones
        </button>
        <button class="px-3 py-1 bg-white text-gray-600 rounded-full text-xs whitespace-nowrap">
          Weather
        </button>
        <button class="px-3 py-1 bg-white text-gray-600 rounded-full text-xs whitespace-nowrap">
          Tasks
        </button>
      </div>
    </div>

    <!-- Map Legend -->
    <div class="absolute bottom-20 left-4 bg-white rounded-lg shadow-lg p-3 z-10">
      <h4 class="font-semibold text-sm text-gray-900 mb-2">Legend</h4>
      <div class="space-y-1">
        <div class="flex items-center space-x-2">
          <div class="w-3 h-3 bg-green-500 rounded-full"></div>
          <span class="text-xs text-gray-600">Active Field</span>
        </div>
        <div class="flex items-center space-x-2">
          <div class="w-3 h-3 bg-blue-500 rounded-full"></div>
          <span class="text-xs text-gray-600">Drone</span>
        </div>
        <div class="flex items-center space-x-2">
          <div class="w-3 h-3 bg-red-500 rounded-full"></div>
          <span class="text-xs text-gray-600">No-Fly Zone</span>
        </div>
        <div class="flex items-center space-x-2">
          <div class="w-3 h-3 bg-yellow-500 rounded-full"></div>
          <span class="text-xs text-gray-600">Maintenance</span>
        </div>
      </div>
    </div>

    <!-- Right Side Tools -->
    <div class="absolute right-4 top-1/2 transform -translate-y-1/2 z-10">
      <div class="bg-white rounded-lg shadow-lg p-2 space-y-2">
        <button class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
          <span>➕</span>
        </button>
        <button class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
          <span>📏</span>
        </button>
        <button class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
          <span>🚫</span>
        </button>
        <button class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
          <span>🌤️</span>
        </button>
        <button class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors">
          <span>📍</span>
        </button>
      </div>
    </div>

    <!-- Field Information Panel -->
    <div class="absolute bottom-20 right-4 left-20 bg-white rounded-lg shadow-lg p-4 z-10">
      <div class="flex items-center justify-between mb-3">
        <h3 class="font-semibold text-gray-900">Field A-3</h3>
        <button class="text-gray-400 hover:text-gray-600">
          <span>✕</span>
        </button>
      </div>
      
      <div class="grid grid-cols-3 gap-3 mb-3">
        <div class="text-center">
          <p class="text-lg font-semibold text-gray-900">45</p>
          <p class="text-xs text-gray-500">Hectares</p>
        </div>
        <div class="text-center">
          <p class="text-lg font-semibold text-gray-900">Rice</p>
          <p class="text-xs text-gray-500">Crop Type</p>
        </div>
        <div class="text-center">
          <p class="text-lg font-semibold text-gray-900">65%</p>
          <p class="text-xs text-gray-500">Complete</p>
        </div>
      </div>

      <div class="flex space-x-2">
        <button class="flex-1 bg-green-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
          View Details
        </button>
        <button class="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
          Navigate
        </button>
      </div>
    </div>

    <!-- Weather Widget -->
    <div class="absolute top-24 right-4 bg-white rounded-lg shadow-lg p-3 z-10">
      <div class="flex items-center space-x-2">
        <span class="text-2xl">☀️</span>
        <div>
          <p class="font-semibold text-gray-900">25°C</p>
          <p class="text-xs text-gray-500">Clear Sky</p>
        </div>
      </div>
      <div class="mt-2 text-xs text-gray-600">
        <p>Wind: 5 km/h</p>
        <p>Humidity: 65%</p>
      </div>
    </div>

    <!-- GPS Coordinates -->
    <div class="absolute top-24 left-4 bg-white rounded-lg shadow-lg p-3 z-10">
      <div class="text-xs text-gray-600">
        <p class="font-semibold text-gray-900 mb-1">Current Location</p>
        <p>Lat: 13.7563° N</p>
        <p>Lng: 100.5018° E</p>
        <p>Alt: 15m</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

onMounted(() => {
  // Initialize map
  // Get user location
  if ('geolocation' in navigator) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('GPS Position:', position.coords)
      },
      (error) => {
        console.error('GPS Error:', error)
      }
    )
  }
})
</script>
